import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"

/**
 * OKS-56 — Creator-side masterclass status pill.
 *
 * Two states only (UX Spec §2.1 — V1 has no `In progress` until OKS-55
 * ships modules):
 *   - completed=true   → variant="default" + "Completed"
 *   - completed=false  → variant="secondary" + "Not started"
 *
 * UX Spec §6 a11y: colour is paired with text label — screen readers
 * read the actual status word, not just the colour. The shadcn `Badge`
 * primitive renders the text directly, so the `aria-label` would be
 * redundant; we keep the wrapper for the deterministic test-id +
 * keyed-variant mapping.
 */
export interface CreatorMasterclassStatusPillProps {
  completed: boolean
  "data-testid"?: string
}

export function CreatorMasterclassStatusPill({
  completed,
  "data-testid": testId,
}: CreatorMasterclassStatusPillProps) {
  const { t } = useTranslation()
  const variant = completed ? "default" : "secondary"

  // Static `t()` calls (not a computed `labelKey`) so the
  // inertia_i18n health scanner can detect both keys.
  const label = completed
    ? t("learn.status_pill.completed")
    : t("learn.status_pill.not_started")

  return (
    <Badge
      variant={variant}
      data-testid={
        testId ?? `learn-status-${completed ? "completed" : "not-started"}`
      }
    >
      {label}
    </Badge>
  )
}
